<template>
  <page>
    <slot name="page-title" slot="page-title"></slot>
    <template slot="page-subtitle" v-if="currentModel">
      Last Model Update: <b>{{ lastModelUpdate | shortDateTime }}</b>
    </template>

    <div
      v-if="mobile"
      class="mobile-cta">
      <span v-if="chamber === 'house'">Click <router-link to="/senate">here</router-link> to view our Senate model.</span>
      <span v-if="chamber === 'senate'">Click <router-link to="/house">here</router-link> to view our House model.</span>
    </div>

    <RouterLink
      v-if="chamber === 'senate'"
      class="banner-link"
      to="/senate/S2022GA03RO">
      <div class="banner">
        Click Here for our Georgia Senate Runoff Forecast
      </div>
    </RouterLink>

    <v-row>
      <v-col cols="12" :order="getOrder('topline')">
        <headline
          :model="currentModel"
          prop="probabilityOfGopMajority"
          :landingPage="true"
          :loading="loading"
          :presidential="false"
          :text="`controlling the ${chamberTitle}`"
          :meanSeat="meanSeat">
        </headline>
      </v-col>
      <v-col cols="12" :order="getOrder('map')" v-if="!runoff">
        <h2 class="mb-2">
          <template>{{ chamber | capitalize }} Seats By State</template>

        </h2>
        <div class="section-description mb-4" v-if="chamber === 'house'">The map below shows the congressional districts for the selected region color coded by win probability (from blue to red, with purple being toss up districts).</div>
        <div class="section-description mb-4" v-if="chamber.includes('senate')">The map below shows the win probability (from blue to red, with purple being toss ups) in each state that has a Senate election. If a state has a regular and a special election, then the special election will be represented as a square on the right side with an '*' symbol. In states where primaries have not been held yet, incumbents and
leading candidates are included.</div>
        <skeleton-loader :loading="loading">
          <div>
            <prediction-legend></prediction-legend>
            <nationwide-map :id="'state-' + chamber + '-map'" :chamber="chamber" :data="landingModels" :loading="loading"></nationwide-map>
          </div>
        </skeleton-loader>
      </v-col>
      <v-col cols="12" :order="getOrder('timeseries')">
        <div>
          <h2 class="mb-4">How Our Model Has Changed</h2>
          <skeleton-loader :loading="loading" class="mb-1">
            <time-series
              :key="`numOfSeatsKey-${chartKey}`"
              :title="'Seats Controlled'"
              @scoreboard-update="scoreboardData = $event"
              @on-mouseout="chartKey += 1"
              :scoreboardData="scoreboardData"
              :data="models"
              :chamber="chamber"
              prop="meanGopSeats"
              name="numOfSeats">
            </time-series>
          </skeleton-loader>
          <skeleton-loader :loading="loading" class="mb-1">
            <time-series
              :key="`seatMajority-${chartKey}`"
              :title="'Chance of a Majority'"
              @scoreboard-update="scoreboardData = $event"
              @on-mouseout="chartKey += 1"
              :scoreboardData="scoreboardData"
              :data="models"
              :chamber="chamber"
              prop="probabilityOfGopMajority"
              name="seatMajority">
            </time-series>
          </skeleton-loader>
        </div>
      </v-col>
      <v-col cols="12" :order="getOrder('sims')">
        <div>
          <h2>14,000,605 Simulations</h2>
          <skeleton-loader :loading="loading">
            <chamber-histogram :chamber="chamber" :currentModel="currentModel" :models="models"></chamber-histogram>
          </skeleton-loader>
        </div>
      </v-col>
      <v-col cols="12" :order="getOrder('scatter')">
        <div>
          <h2>
            <template>
              Individual {{ chamberTitle | capitalize }} Seats
            </template>
          </h2>
          <div class="section-description mb-4">The chart below displays the probability of each party winning individual races. The further to the left, the higher the chance of a Democratic victory and the further to the right the higher to chance for a GOP victory.</div>
          <skeleton-loader :loading="loading">
            <scatter-table
              :data="landingModels"
              :chamber="chamber"
              :loading="loading">
            </scatter-table>
          </skeleton-loader>
        </div>
      </v-col>
    </v-row>
  </page>
</template>

<script>
import { mapGetters } from 'vuex'

import { NationwideMap, ScatterTable, TimeSeries } from '@/components/charts'
import Loading from '@/components/common/Loading'
import { ButtonToggle, Headline, SkeletonLoader } from '@/components/common'
import PredictionLegend from '@/components/common/PredictionLegend'
import Page from './Page'

const name = 'landing_page_wrapper'

const props = {
  chamber: {
    type: String,
    required: true
  },
  chamberTitle: {
    type: String,
    required: true
  },
  models: {
    type: Array,
    required: true
  },
  currentModel: {
    type: Object,
    required: true
  },
  landingModels: {
    type: Array,
    required: true
  },
  loading: {
    type: Boolean,
    required: true
  },
  runoff: {
    type: Boolean,
    required: false,
    default: false
  },
  lastModelUpdate: {
    required: true,
    type: String,
  },
  meanSeat: {
    default: "",
    required: true,
    type: String,
  },
}

const components = {
  ButtonToggle,
  Headline,
  'chamber-histogram': () => import('./common/ChamberHistogram'),
  Loading,
  'login-wrapper': () => import('./common/LoginWrapper'),
  NationwideMap,
  Page,
  PredictionLegend,
  ScatterTable,
  SkeletonLoader,
  TimeSeries
}

const computed = {
  ...mapGetters({
    mobile: 'ux/mobile',
    screenWidth: 'ux/screenWidth',
    dates: 'aggregateModels/dates'
  })
}

const methods = {
  getOrder (name) {
    const order = [
      'topline',
      'map',
      'timeseries',
      'scatter',
      'sims',
    ];
    return order.indexOf(name) + 1
  }
}

export default {
  name,
  props,
  computed,
  methods,
  components,
  data () {
    return {
      scoreboardData: {},
      chartKey: 0
    }
  }
}
</script>

<style>
#map-legend-wrapper {
  width: 100%;
  height: 70px;
  position: relative;
  margin: 10px 0;
}

.mobile-cta {
  align-items: center;
  background-color: #2223;
  border-radius: .25em;
  display: flex;
  justify-content: center;
  padding: 1em;
}

.theme--dark .mobile-cta {
  background-color: #222;
}

.banner {
  background-color: var(--gop);
  border-radius: .5em;
  color: white;
  font-size: 1.25em;
  font-weight: bold;
  padding: 1.5em;
  text-align: center;
}

.banner-link {
  display: block;
  margin: 1em 0;
  text-decoration: none;
}
</style>
